import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { i18n } from 'i18n';
import {
  getBulkEditSceneState,
  subscribeToBulkEditSceneState
} from '../Scenes/BulkEditScene/bulkEditSceneState';
import {
  getMeasureState,
  subscribeToMeasureState,
  MeasureMode
} from '../Scenes/BulkEditScene/Toolbar/measureState';

const MEASURE_MESSAGES: Record<MeasureMode, string> = {
  'node-to-node': 'Measure: click two nodes to measure the straight-line distance',
  'spool-click': 'Measure: click a spool to measure its length',
  route: 'Measure: click two nodes to measure the shortest route between them',
  'route-volume': 'Route volume: click two nodes to measure the internal volume of the route between them',
};

const ToolStatusChip = () => {
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);
  const [dismissedMessage, setDismissedMessage] = React.useState<string | null>(null);
  const { activeToolMessage } = getBulkEditSceneState();
  const { measureActive, measureMode } = getMeasureState();
  const message = activeToolMessage ?? (measureActive ? i18n._(MEASURE_MESSAGES[measureMode]) : null);
  const [title, ...rest] = (message ?? '').split(': ');
  const body = rest.join(': ');

  React.useEffect(() => subscribeToBulkEditSceneState(() => forceUpdate()), []);
  React.useEffect(() => subscribeToMeasureState(() => forceUpdate()), []);

  React.useEffect(() => {
    if (!message) setDismissedMessage(null);
  }, [message]);

  if (!message || message === dismissedMessage) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 'calc(var(--stopes-drawer-width, 0px) + 16px)',
        zIndex: 1200,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1.5,
        py: 1,
        maxWidth: 380,
        bgcolor: 'primary.dark',
        color: 'primary.contrastText',
        borderRadius: 1,
      }}
    >
      <Box>
        <Typography variant="caption" sx={{ display: 'block', fontSize: 12, fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', fontSize: 12 }}>
          {body}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', fontSize: 11, opacity: 0.7 }}>
          {i18n._('Press Esc to cancel')}
        </Typography>
      </Box>
      <IconButton
        size="small"
        aria-label={i18n._('Dismiss tool message')}
        onClick={() => setDismissedMessage(message)}
        sx={{ color: 'inherit', p: 0.25 }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Paper>
  );
};

export default ToolStatusChip;
